import type { TradeResultSyncConfig } from 'api/elplan/elplan.types'
import {
  LAST_SYNC_TIME_QUERY_KEY,
  TRADE_RESULT_SYNC_QUERY_KEY,
  useLastSyncTimeQuery,
  useSyncTradeResultMutation,
} from 'api/elplan/intradayTrading.api'
import { queryClient } from 'helpers/queryClient'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'

export function useTradeResultSyncBehavior(
  systemId: number | undefined,
  activeSubtype: string | undefined,
  tradeResultSyncConfig: TradeResultSyncConfig | undefined
) {
  const { t } = useTranslation()
  const { mutate: syncTradeResult, isPending: isSyncing } = useSyncTradeResultMutation()
  const { data: lastSyncTimeData } = useLastSyncTimeQuery(systemId, tradeResultSyncConfig !== undefined)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [isTradeResultFetching, setIsTradeResultFetching] = useState(false)

  const handleSyncClick = () => {
    if (!systemId) return
    syncTradeResult(
      { system: systemId, ...(activeSubtype ? { metadata: { subtype: activeSubtype } } : {}) },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: [TRADE_RESULT_SYNC_QUERY_KEY] })
          void queryClient.invalidateQueries({ queryKey: [LAST_SYNC_TIME_QUERY_KEY, systemId] })
        },
        onError: () => {
          alertStore.error(t('Failed to sync trade result. Please try again or contact support.'))
        },
      }
    )
  }

  const handleRefreshClick = () => {
    if (!systemId) return
    void queryClient.invalidateQueries({ queryKey: [TRADE_RESULT_SYNC_QUERY_KEY] })
  }

  const handleTradeResultFetched = () => {
    setLastFetchedAt(new Date())
  }

  const handleTradeResultFetchingChange = (isFetching: boolean) => {
    setIsTradeResultFetching(isFetching)
  }

  return {
    isSyncing,
    lastSyncTimeData,
    lastFetchedAt,
    isTradeResultFetching,
    handleSyncClick,
    handleRefreshClick,
    handleTradeResultFetched,
    handleTradeResultFetchingChange,
  }
}
